import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';

// ─── NOVAA dark tokens ───────────────────────────────────────────
const T = {
  bg: 'var(--nd-bg)', surface: 'var(--nd-surface)', border: 'var(--nd-w06)',
  text: 'var(--nd-text)', textMuted: 'var(--nd-tx35)',
};
const API = process.env.REACT_APP_API_BASE_URL;
const IMG_BASE = API ? API.replace('/api', '') : '';

const GLOBAL = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700;9..40,800&family=DM+Mono:wght@400;500&display=swap');
  .nv-pc * { box-sizing: border-box; }
  .nv-pc ::-webkit-scrollbar { width: 6px; height: 6px; }
  .nv-pc ::-webkit-scrollbar-thumb { background: var(--nd-w08); border-radius: 4px; }
  .nv-pc input, .nv-pc select { background: var(--nd-w05); border: 1px solid var(--nd-w10); border-radius: 8px; color: var(--nd-text); font-family: 'DM Sans', sans-serif; font-size: 13px; padding: 8px 12px; outline: none; }
  .nv-pc input:focus, .nv-pc select:focus { border-color: rgba(96,165,250,0.5); }
  .nv-pc select option { background: var(--nd-surface-2); color: var(--nd-text); }
  .nv-pc input[type="date"] { color-scheme: dark; }
  .nv-pc-btn { border: none; border-radius: 8px; cursor: pointer; font-family: 'DM Sans', sans-serif; font-size: 12px; font-weight: 700; padding: 7px 14px; transition: opacity 0.15s; }
  .nv-pc-btn:hover { opacity: 0.88; }
`;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const fmtDate = (s) => { if (!s) return '—'; const [y, m, d] = String(s).split('-'); return (y && m && d) ? `${MONTHS[Number(m) - 1]} ${Number(d)}, ${y}` : s; };
const money = (n) => `PKR ${Number(n || 0).toLocaleString()}`;
const receiptUrl = (p) => (p ? `${IMG_BASE}${p}` : null);

const STATUS_COLORS = {
  Pending: '#F59E0B', Approved: '#22D3A5', Rejected: '#F87171',
  pending: '#94A3B8', submitted: '#60A5FA', approved: '#22D3A5', rejected: '#F87171',
};
const Badge = ({ value }) => (
  <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.05em', color: STATUS_COLORS[value] || T.textMuted, background: (STATUS_COLORS[value] || '#888') + '20', border: `1px solid ${(STATUS_COLORS[value] || '#888')}40`, padding: '2px 8px', borderRadius: 5 }}>
    {String(value || '—').toUpperCase()}
  </span>
);

// ─── Ledger (expense breakdown) review panel ─────────────────────
const LedgerPanel = ({ request, onChanged }) => {
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState({});

  const fetchLines = useCallback(async () => {
    try {
      setLoading(true);
      const r = await axios.get(`${API}/ci-petty-cash/request/${request.id}/ledger`);
      setLines(r.data?.lines || []);
    } catch { setLines([]); } finally { setLoading(false); }
  }, [request.id]);

  useEffect(() => { fetchLines(); }, [fetchLines]);

  const amend = async (line, body) => {
    try {
      await axios.patch(`${API}/ci-petty-cash/ledger/${line.id}/amend`, { ...body, amended_by: 'finance' });
      await fetchLines();
      if (onChanged) onChanged();
    } catch (err) {
      alert(`❌ ${err?.response?.data?.message || 'Failed to amend'}`);
    }
  };

  const setExpenseStatus = async (status) => {
    try {
      await axios.post(`${API}/ci-petty-cash/request/${request.id}/expense-approval`, { status });
      if (onChanged) onChanged();
    } catch (err) { alert(`❌ ${err?.response?.data?.message || 'Failed'}`); }
  };

  const isExpenseFinalized = request.expense_status === 'approved' || request.expense_status === 'rejected';

  return (
    <div style={{ borderTop: `1px solid ${T.border}`, padding: '12px 16px', background: 'var(--nd-w02)' }}>
      <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.14em', color: T.textMuted, marginBottom: 10 }}>EXPENSE BREAKDOWN</div>
      {loading ? (
        <div style={{ color: T.textMuted, fontSize: 12, padding: '10px 0' }}>Loading…</div>
      ) : lines.length === 0 ? (
        <div style={{ color: T.textMuted, fontSize: 12, padding: '10px 0' }}>No expenses submitted yet.</div>
      ) : (
        <>
          {lines.map((l) => {
            const reduced = l.original_amount != null && Number(l.amount) < Number(l.original_amount);
            const rUrl = receiptUrl(l.receipt_url);
            return (
              <div key={l.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: `1px solid ${T.border}`, opacity: l.status === 'rejected' ? 0.5 : 1 }}>
                {rUrl ? (
                  <img src={rUrl} alt="receipt" onClick={() => window.open(rUrl, '_blank')} style={{ width: 34, height: 34, borderRadius: 6, objectFit: 'cover', cursor: 'pointer', border: '1px solid var(--nd-w12)' }} />
                ) : (
                  <div style={{ width: 34, height: 34, borderRadius: 6, background: 'var(--nd-w04)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: T.textMuted }}>—</div>
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ fontSize: 9, fontWeight: 700, color: '#60A5FA', background: 'rgba(96,165,250,0.12)', padding: '2px 7px', borderRadius: 5 }}>{l.expense_type || 'Other'}</span>
                  {l.description ? <span style={{ fontSize: 12, color: T.text, marginLeft: 8 }}>{l.description}</span> : null}
                  <div style={{ fontSize: 9, color: T.textMuted, marginTop: 3 }}>
                    by {l.added_by || '—'}{reduced ? ` · was ${money(l.original_amount)}` : ''}{l.amended_by ? ` · amended by ${l.amended_by}` : ''}
                  </div>
                </div>
                
                {isExpenseFinalized ? (
                   <div style={{ width: 100, textAlign: 'right', fontSize: 13, fontWeight: 600 }}>{money(l.amount)}</div>
                ) : (
                  <>
                    <input
                      type="number" min="0"
                      value={edit[l.id] ?? l.amount}
                      onChange={(e) => setEdit((p) => ({ ...p, [l.id]: e.target.value }))}
                      style={{ width: 100, textAlign: 'right' }}
                      disabled={l.status === 'rejected'}
                    />
                    <button className="nv-pc-btn" style={{ background: 'rgba(96,165,250,0.15)', color: '#60A5FA' }}
                      title="Reduce to this amount (cannot increase)"
                      onClick={() => amend(l, { amount: Number(edit[l.id] ?? l.amount) })}>↓ Save</button>
                    <button className="nv-pc-btn" style={{ background: l.status === 'rejected' ? 'rgba(148,163,184,0.15)' : 'rgba(248,113,113,0.12)', color: l.status === 'rejected' ? '#94A3B8' : '#F87171' }}
                      onClick={() => amend(l, { status: l.status === 'rejected' ? 'submitted' : 'rejected' })}>
                      {l.status === 'rejected' ? 'Restore' : 'Reject'}
                    </button>
                  </>
                )}
              </div>
            );
          })}
          
          {!isExpenseFinalized && lines.length > 0 && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
              <button className="nv-pc-btn" style={{ background: 'rgba(248,113,113,0.12)', color: '#F87171' }} onClick={() => setExpenseStatus('rejected')}>Reject report</button>
              <button className="nv-pc-btn" style={{ background: 'linear-gradient(135deg,#3B82F6,#22D3A5)', color: 'var(--nd-text-strong)' }} onClick={() => setExpenseStatus('approved')}>Approve report</button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const CIPettyCash = () => {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [status, setStatus] = useState('');
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false); // Export loader
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(null);

  const fetchRequests = useCallback(async () => {
    try {
      setLoading(true); setError(null);
      const qs = new URLSearchParams();
      if (from) qs.append('from', from);
      if (to) qs.append('to', to);
      
      // Agar standard status hai (Pending, Approved, Rejected) tou backend ko bhejein
      if (status && status !== 'expense_pending') qs.append('status', status);
      
      const q = qs.toString() ? `?${qs.toString()}` : '';
      const r = await axios.get(`${API}/ci-petty-cash/requests${q}`);
      
      let fetchedRequests = r.data?.requests || [];

      // Agar custom filter "expense_pending" select kiya hai, tou yahan frontend pe filter kar lein
      if (status === 'expense_pending') {
        fetchedRequests = fetchedRequests.filter(req => req.expense_status === 'submitted');
      }

      setRequests(fetchedRequests);
    } catch (err) { 
      setError('Failed to load petty cash requests'); 
      setRequests([]); 
    }
    finally { setLoading(false); }
  }, [from, to, status]);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  const setApproval = async (id, st) => {
    try {
      await axios.post(`${API}/ci-petty-cash/request/${id}/approval`, { status: st });
      fetchRequests();
    } catch (err) { alert(`❌ ${err?.response?.data?.message || 'Failed'}`); }
  };

  // Advanced CSV Export Function with Breakdown Data
  const exportToCSV = async () => {
    if (requests.length === 0) {
      alert('Export karne ke liye koi data nahi hai!');
      return;
    }

    setExporting(true);
    try {
      const requestsWithLedgers = await Promise.all(
        requests.map(async (r) => {
          try {
            const res = await axios.get(`${API}/ci-petty-cash/request/${r.id}/ledger`);
            return { ...r, lines: res.data?.lines || [] };
          } catch (e) {
            return { ...r, lines: [] };
          }
        })
      );

      const headers = [
        'ID', 'Team', 'Date', 'Site / Clients', 'Jobs', 'Total Requested Amount', 
        'Cash Status', 'Expense Status', 
        'Expense Type', 'Expense Description', 'Expense Amount', 'Added By'
      ];
      
      const rows = [];

      requestsWithLedgers.forEach(r => {
        const teamName = `"${(r.team_lead_name || `Team ${r.team_id}`).replace(/"/g, '""')}"`;
        const dateVal = `"${r.for_date ? fmtDate(r.for_date) : '—'}"`;
        const siteNames = `"${(r.clients || 'No jobs that day').replace(/"/g, '""')}"`;

        const baseData = [
          r.id,
          teamName, 
          dateVal,  
          siteNames,
          r.job_count || 0,
          r.amount || 0,
          r.status || 'Pending',
          r.expense_status || 'Pending'
        ];

        if (r.lines && r.lines.length > 0) {
          r.lines.forEach(line => {
            const expType = `"${(line.expense_type || 'Other').replace(/"/g, '""')}"`;
            const expDesc = `"${(line.description || '').replace(/"/g, '""')}"`;
            const expAmount = line.amount || 0;
            const expAddedBy = `"${(line.added_by || '—').replace(/"/g, '""')}"`;
            
            rows.push([...baseData, expType, expDesc, expAmount, expAddedBy].join(','));
          });
        } else {
          rows.push([...baseData, '""', '""', '""', '""'].join(','));
        }
      });

      const csvContent = [headers.join(','), ...rows].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      const fileName = `PettyCash_Breakdown_${from || 'All'}_to_${to || 'All'}.csv`;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

    } catch (err) {
      alert('CSV export karne mein masla pesh aaya. Dobara koshish karein.');
    } finally {
      setExporting(false);
    }
  };

  const card = { background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, overflow: 'hidden' };

  return (
    <div className="nv-pc" style={{ background: T.bg, fontFamily: "'DM Sans', sans-serif", color: T.text, padding: '14px 18px', minHeight: '100vh' }}>
      <style>{GLOBAL}</style>

      <div style={{ ...card, marginBottom: 14 }}>
        <div style={{ padding: '20px 24px', borderBottom: `1px solid ${T.border}` }}>
          <div style={{ fontSize: 9, color: T.textMuted, letterSpacing: '0.18em', marginBottom: 5 }}>NOVAA · FINANCE</div>
          <div style={{ fontSize: 26, fontWeight: 800, color: 'var(--nd-text-strong)', letterSpacing: '-0.03em', lineHeight: 1 }}>Petty Cash — C&I</div>
          <div style={{ fontSize: 12, color: T.textMuted, marginTop: 5 }}>Approve cash, review &amp; amend daily expense breakdowns</div>
        </div>
        <div style={{ height: 2, background: 'linear-gradient(90deg,#3B82F6,#22D3A5,transparent)' }} />
        <div style={{ padding: '12px 22px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            <span style={{ color: T.textMuted, fontSize: 12 }}>→</span>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
          <select value={status} onChange={(e) => setStatus(e.target.value)} style={{ minWidth: 150 }}>
            <option value="">All statuses</option>
            <option value="Pending">Pending (Cash)</option>
            <option value="Approved">Approved (Cash)</option>
            <option value="Rejected">Rejected (Cash)</option>
            {/* Yahan naya filter option add kiya hai */}
            <option value="expense_pending">Breakdown Submitted</option>
          </select>
          
          <span style={{ marginLeft: 'auto', fontSize: 11, color: T.textMuted, fontFamily: "'DM Mono', monospace" }}>{requests.length} request{requests.length !== 1 ? 's' : ''}</span>
          
          <button 
            onClick={exportToCSV} 
            disabled={exporting}
            className="nv-pc-btn" 
            style={{ background: 'var(--nd-w06)', color: 'var(--nd-text)', border: `1px solid var(--nd-w10)`, opacity: exporting ? 0.6 : 1, cursor: exporting ? 'wait' : 'pointer' }}
          >
            {exporting ? '⏳ Generating...' : '📥 Export CSV'}
          </button>
        </div>
      </div>

      {error && <div style={{ ...card, padding: '14px 20px', marginBottom: 14, color: '#F87171', fontSize: 13 }}>⚠ {error}</div>}

      {loading ? (
        <div style={{ ...card, padding: '50px 0', textAlign: 'center', color: T.textMuted, fontSize: 13 }}>Loading…</div>
      ) : requests.length === 0 ? (
        <div style={{ ...card, padding: '50px 0', textAlign: 'center', color: T.textMuted }}>No petty cash requests for these filters</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {requests.map((r) => (
            <div key={r.id} style={card}>
              <div style={{ padding: '16px 18px', display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 220 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                    <span style={{ fontSize: 15, fontWeight: 800, color: 'var(--nd-text-strong)' }}>{r.team_lead_name || `Team ${r.team_id}`}</span>
                    <span style={{ fontSize: 11, color: T.textMuted, fontFamily: "'DM Mono', monospace" }}>{fmtDate(r.for_date)}</span>
                  </div>
                  <div style={{ fontSize: 12, color: T.textMuted }}>
                    {r.clients || 'No jobs that day'} · {r.job_count} job{r.job_count !== 1 ? 's' : ''}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 17, fontWeight: 800, color: 'var(--nd-text-strong)', fontFamily: "'DM Mono', monospace" }}>{money(r.amount)}</div>
                  <div style={{ fontSize: 10, color: T.textMuted }}>spent {money(r.spent)}</div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 5, alignItems: 'flex-end' }}>
                  <div style={{ display: 'flex', gap: 6 }}><Badge value={r.status} /><Badge value={r.expense_status} /></div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    
                    {r.status === 'Pending' && (
                      <>
                        <button className="nv-pc-btn" style={{ background: 'linear-gradient(135deg,#3B82F6,#22D3A5)', color: 'var(--nd-text-strong)' }} onClick={() => setApproval(r.id, 'Approved')}>Approve cash</button>
                        <button className="nv-pc-btn" style={{ background: 'rgba(248,113,113,0.12)', color: '#F87171' }} onClick={() => setApproval(r.id, 'Rejected')}>Reject</button>
                      </>
                    )}
                    
                    <button className="nv-pc-btn" style={{ background: 'var(--nd-w06)', color: T.textMuted }} onClick={() => setExpanded(expanded === r.id ? null : r.id)}>
                      {expanded === r.id ? 'Hide' : 'Breakdown'}{r.line_count ? ` (${r.line_count})` : ''}
                    </button>
                  </div>
                </div>
              </div>
              {expanded === r.id && <LedgerPanel request={r} onChanged={fetchRequests} />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CIPettyCash;